import { useState, SyntheticEvent, useCallback } from 'react';
import { apiPath } from 'presentations/routes/path';
import { useRequest } from 'presentations/views/hooks/request';
import { SubscriptionStateItem } from 'presentations/api/schema/subscription';
import { ViewerItem } from 'presentations/api/schema/user';
import RoutingFormFeature from 'domains/models/subscription/features/routing_form';
import EventTypeFeature from 'domains/models/subscription/features/event_type';
import { setEvent } from 'presentations/views/helpers/analytics';

interface Props {
  viewer: ViewerItem | null;
}

export function useCreateMenu(props: Props) {
  const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [isShowingPlanUpgrade, setIsShowingPlanUpgrade] = useState<boolean>(
    false,
  );

  const subscriptionStateRequest = useRequest(
    !props.viewer
      ? null
      : apiPath(`/v1/teams/${props.viewer.team!.uid}/subscriptions/state`),
  );
  const subscriptionState: SubscriptionStateItem | null =
    subscriptionStateRequest.data;
  let canAddEventType = false;
  let canAddForm = false;
  if (subscriptionState) {
    if (props.viewer) {
      const eventTypeFeature = new EventTypeFeature({
        planVersion: props.viewer.planVersion,
      });
      canAddEventType = eventTypeFeature.canAdd(
        subscriptionState.planId,
        subscriptionState.viewerEnabledEventTypeCount,
      );

      const routingFormFeature = new RoutingFormFeature({
        planVersion: props.viewer.planVersion,
      });
      canAddForm = routingFormFeature.canAdd(
        subscriptionState.planId,
        subscriptionState.enabledRoutingFormCount,
      );
    }
  }

  const onClickCreateMenu = (
    e: SyntheticEvent<HTMLButtonElement | HTMLDivElement>,
  ) => {
    e.preventDefault();
    setCreateMenuAnchor(e.currentTarget);
    setEvent('dashboard header click create');
  };
  const onCloseCreateMenu = () => {
    setCreateMenuAnchor(null);
  };

  const onClosePlanUpgrade = () => {
    setIsShowingPlanUpgrade(false);
    setEvent('create menu click plan upgrade');
  };

  const onClickNewEventType = useCallback(
    (e: SyntheticEvent<HTMLAnchorElement>) => {
      setEvent('create menu click new event type');
      if (!canAddEventType) {
        e.preventDefault();
        setIsShowingPlanUpgrade(true);
      }
    },
    [canAddEventType],
  );

  const onClickNewForm = useCallback(
    (e: SyntheticEvent<HTMLAnchorElement>) => {
      setEvent('create menu click new form');
      if (!canAddForm) {
        e.preventDefault();
        setIsShowingPlanUpgrade(true);
      }
    },
    [canAddForm],
  );

  return {
    state: {
      createMenuAnchor,
      isShowingPlanUpgrade,
    },
    actions: {
      onClickCreateMenu,
      onCloseCreateMenu,
      onClosePlanUpgrade,
      onClickNewEventType,
      onClickNewForm,
    },
  };
}
